/**
 * =============================================================================
 * FALCON - Module Deneigement des Parkings Avion
 * =============================================================================
 *
 * @file        metarService.ts
 * @description Service pour la recuperation des donnees METAR via l'API
 *              Les donnees METAR sont mises a jour toutes les 30 minutes (UTC)
 *
 * @version     2.3
 * @author      Equipe FALCON
 * =============================================================================
 */

import { apiClient } from './api';

/**
 * Structure des donnees de vent
 */
export interface MetarWind {
  speed_kt: number | null;
  speed_kmh: number | null;
  direction: number | null;
  direction_text: string;
  gust_kt: number | null;
  gust_kmh: number | null;
}

/**
 * Structure des risques hiver
 */
export interface MetarRisques {
  givrage: boolean;
  verglas: boolean;
  neige_active: boolean;
}

/**
 * Structure des operations
 */
export interface MetarOperations {
  conditions_operables: boolean;
  snoclo: boolean;
  cavok: boolean;
}

/**
 * Structure de l'alerte
 */
export interface MetarAlert {
  level: 'normal' | 'vigilance' | 'warning' | 'critique';
  message: string;
}

/**
 * Structure des indicateurs hiver
 */
export interface MetarWinterIndicators {
  spread_temp_dewpoint: number | null;
  risque_givrage: boolean;
  risque_verglas: boolean;
  neige_active: boolean;
  conditions_operables: boolean;
  alert_level: 'normal' | 'vigilance' | 'warning' | 'critique';
  alert_message: string;
}

/**
 * Structure complete des donnees METAR
 */
export interface MetarData {
  // Identifiants
  icao: string;
  airport_name: string;

  // METAR brut
  raw: string;
  metar_tranche: string;
  observed_at_utc: string;
  observed_at_local: string;
  observed_time_utc: string;
  observed_time_local: string;

  // Donnees principales
  temperature: number | null;
  dewpoint: number | null;
  humidity: number | null;
  visibility: number | null;
  qnh: number | null;

  // Vent
  wind_speed: number | null;
  wind_speed_kmh: number | null;
  wind_dir: number | null;
  wind_dir_text: string;
  wind_gust: number | null;
  wind_gust_kmh: number | null;

  // Conditions
  weather: string | null;
  weather_report: string | null;
  cavok: boolean;
  snoclo: boolean;

  // Nuages
  clouds: Array<{ type: string; height: number }>;
  ceiling: number | null;

  // Soleil
  sunrise: string | null;
  sunset: string | null;

  // Indicateurs hiver
  winter_indicators: MetarWinterIndicators;

  // Timestamp
  fetched_at: string;
}

/**
 * Structure des donnees hiver detaillees
 */
export interface MetarWinterData {
  icao: string;
  airport_name: string;
  observed_at_utc: string;
  observed_at_local: string;
  metar_tranche: string;

  // Donnees critiques
  temperature: number | null;
  dewpoint: number | null;
  spread_temp_dewpoint: number | null;
  humidity: number | null;
  visibility: number | null;
  qnh: number | null;

  // Vent
  wind: MetarWind;

  // Conditions
  conditions: string | null;
  risques: MetarRisques;

  // Plafond
  ceiling: number | null;
  clouds: Array<{ type: string; height: number }>;

  // Operations
  operations: MetarOperations;

  // Alerte
  alert: MetarAlert;

  // Soleil
  sun: {
    sunrise: string | null;
    sunset: string | null;
  };

  // Priorite recommandee
  recommended_priority: 'normale' | 'urgente' | 'critique';
}

/**
 * Reponse API METAR
 */
interface MetarApiResponse {
  success: boolean;
  data: MetarData;
  error?: string;
  fallback?: {
    icao: string;
    message: string;
    timestamp: string;
  };
}

/**
 * Reponse API METAR Winter
 */
interface MetarWinterApiResponse {
  success: boolean;
  data: MetarWinterData;
  error?: string;
}

/**
 * Service METAR pour recuperation des donnees meteorologiques
 */
export const metarService = {
  /**
   * Recupere les donnees METAR actuelles pour un aeroport
   * @param icao Code ICAO de l'aeroport (defaut: LFPO pour Orly)
   */
  async getCurrent(icao: string = 'LFPO'): Promise<MetarData | null> {
    try {
      const response = await apiClient.get<MetarApiResponse>(`/metar/${icao}`);
      if (response.data.success) {
        return response.data.data;
      }
      console.warn('METAR API error:', response.data.error);
      return null;
    } catch (error) {
      console.error('Failed to fetch METAR:', error);
      return null;
    }
  },

  /**
   * Recupere les indicateurs hiver detailles
   * @param icao Code ICAO de l'aeroport
   */
  async getWinterIndicators(icao: string = 'LFPO'): Promise<MetarWinterData | null> {
    try {
      const response = await apiClient.get<MetarWinterApiResponse>(`/metar/${icao}/winter`);
      if (response.data.success) {
        return response.data.data;
      }
      console.warn('METAR Winter API error:', response.data.error);
      return null;
    } catch (error) {
      console.error('Failed to fetch METAR Winter:', error);
      return null;
    }
  },

  /**
   * Force le rafraichissement des donnees METAR
   * @param icao Code ICAO de l'aeroport
   */
  async refresh(icao: string = 'LFPO'): Promise<MetarData | null> {
    try {
      const response = await apiClient.post<MetarApiResponse>(`/metar/refresh?icao=${icao}`);
      if (response.data.success) {
        return response.data.data;
      }
      return null;
    } catch (error) {
      console.error('Failed to refresh METAR:', error);
      return null;
    }
  },
};

export default metarService;
